var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import { getFloat } from "../../helpers/formatNumber";
import { PlayModeType, } from "./GameContext.types";
import { BetIncreaseSelect } from "../ControlPanel/BetIncrease";
var GameContext = createContext(undefined);
GameContext.displayName = "GameContext";
export var useGameContext = function () {
    var context = useContext(GameContext);
    if (!context) {
        throw new Error("useGameContext must be used within a GameContextProvider");
    }
    return context;
};
export var GameContextProvider = function (_a) {
    var children = _a.children, initialBalance = _a.initialBalance, initialGameResults = _a.initialGameResults, constants = _a.constants;
    var RTP = constants.RTP, MIN_PREDICTION = constants.MIN_PREDICTION, MAX_PREDICTION = constants.MAX_PREDICTION, RESULTS_SHOWN_AMOUNT = constants.RESULTS_SHOWN_AMOUNT;
    var _b = useState({ width: 0, height: 0 }), boardSize = _b[0], setBoardSize = _b[1];
    var _c = useState(initialBalance), balance = _c[0], setBalance = _c[1];
    var _d = useState(0), betAmount = _d[0], setBetAmount = _d[1];
    var _e = useState(PlayModeType.Manual), playMode = _e[0], setPlayMode = _e[1];
    var _f = useState('0'), prediction = _f[0], setPrediction = _f[1];
    var _g = useState(false), inProgress = _g[0], setInProgress = _g[1];
    var _h = useState(initialGameResults), gameResults = _h[0], setGameResults = _h[1];
    var defaultPayout = parseFloat(prediction);
    var minPayout = MIN_PREDICTION;
    var maxPayout = MAX_PREDICTION;
    var _j = useState(getFloat(defaultPayout)), payout = _j[0], setPayout = _j[1];
    var defaultWinChance = (100 / parseFloat(prediction)) * RTP;
    var _k = useState(getFloat(defaultWinChance)), winChance = _k[0], setWinChance = _k[1];
    var _l = useState(payout), payoutInput = _l[0], setPayoutInput = _l[1];
    var _m = useState(winChance), winChanceInput = _m[0], setWinChanceInput = _m[1];
    var _o = useState({
        betCount: -1,
        betDeltaWinAction: "Reset",
        betDeltaWin: 0,
        betDeltaLossAction: "Reset",
        betDeltaLoss: 0,
        stopWin: 0,
        stopLoss: 0,
    }), autoPlayOptions = _o[0], setAutoPlayOptions = _o[1];
    var _p = useState(false), autoPlayInProgress = _p[0], setAutoPlayInProgress = _p[1];
    var initialAutoPlayBetCount = useRef(-1);
    var initialAutoPlayBetAmount = useRef(0);
    var initialAutoPlayBalance = useRef(0);
    var _q = useState(false), autoPlayStartGame = _q[0], setAutoPlayStartGame = _q[1];
    var _r = useState(false), autoPlayNextBet = _r[0], setAutoPlayNextBet = _r[1];
    var stopAutoPlay = useRef(false);
    var _s = useState(null), prevAutoPlayWinnings = _s[0], setPrevAutoPlayWinnings = _s[1];
    var minBet = 0;
    var maxBet = 1000;
    var _t = useState(false), randomSelect = _t[0], setRandomSelect = _t[1];
    var _u = useState(false), cashout = _u[0], setCashout = _u[1];
    var _v = useState(0), cashoutMultiplier = _v[0], setCashoutMultiplier = _v[1];
    var _w = useState(false), controlPanelDisabled = _w[0], setControlPanelDisabled = _w[1];
    var _x = useState(false), betButtonShake = _x[0], setBetButtonShake = _x[1];
    var _y = useState([]), betButtonTooltip = _y[0], setBetButtonTooltip = _y[1];
    var _z = useState(""), clientSeed = _z[0], setClientSeed = _z[1];
    var _0 = useState(false), customClientSeed = _0[0], setCustomClientSeed = _0[1];
    var generateClientSeed = function () {
        var arr = new Uint8Array(48);
        window.crypto.getRandomValues(arr);
        var seed = Array.from(arr, function (x) { return x.toString(16).padStart(2, "0"); }).join("");
        setClientSeed(seed);
        setCustomClientSeed(false);
        return seed;
    };
    useEffect(function () {
        generateClientSeed();
    }, []);
    var rotateGameResults = function (newItem) {
        var updatedResults = __spreadArray([newItem], gameResults, true);
        if (updatedResults.length > RESULTS_SHOWN_AMOUNT) {
            updatedResults.length = RESULTS_SHOWN_AMOUNT;
        }
        setGameResults(updatedResults);
    };
    var returnParsedValue = function (e) {
        var input = e.target.value;
        var value = parseFloat(input);
        return value;
    };
    var updatePayout = useCallback(function (e) {
        var value = returnParsedValue(e);
        if (isNaN(value) || value <= 0) {
            value = defaultPayout;
        }
        if (minPayout && value < minPayout) {
            value = minPayout;
        }
        if (maxPayout && value > maxPayout) {
            value = maxPayout;
        }
        var newWinChance = (100 / value) * RTP;
        var calculatedPayout = getFloat(value, 4);
        var calculatedWinChance = getFloat(newWinChance);
        setPayout(calculatedPayout);
        setPayoutInput(calculatedPayout);
        setPrediction(value.toString());
        setWinChance(calculatedWinChance);
        setWinChanceInput(calculatedWinChance);
    }, [setPayout, setPrediction, setWinChance, defaultPayout, maxPayout, minPayout, RTP]);
    var updateWinChance = useCallback(function (e) {
        var value = returnParsedValue(e);
        if (isNaN(value) || value <= 0) {
            value = defaultWinChance;
        }
        if (MIN_PREDICTION && value < MIN_PREDICTION) {
            value = MIN_PREDICTION;
        }
        if (MAX_PREDICTION && value > MAX_PREDICTION) {
            value = MAX_PREDICTION;
        }
        var newPayout = (100 / value) * RTP;
        var calculatedPayout = getFloat(newPayout, 4);
        var calculatedWinChance = getFloat(value);
        setPayout(calculatedPayout);
        setPayoutInput(calculatedPayout);
        setPrediction(getFloat(newPayout));
        setWinChance(calculatedWinChance);
        setWinChanceInput(calculatedWinChance);
    }, [setPayout, setPrediction, setWinChance, defaultWinChance, RTP, MIN_PREDICTION, MAX_PREDICTION]);
    // Finalize auto bet
    var finalizeAutoBet = useCallback(function () {
        setInProgress(false);
        if (playMode === PlayModeType.Auto) {
            setAutoPlayNextBet(false);
            stopAutoPlay.current = true;
            setAutoPlayInProgress(false);
            setAutoPlayOptions(function (prevOptions) { return (__assign(__assign({}, prevOptions), { betCount: initialAutoPlayBetCount.current })); });
            setBetAmount(initialAutoPlayBetAmount.current);
            initialAutoPlayBalance.current = 0;
            setPrevAutoPlayWinnings(null);
        }
    }, [playMode, betAmount, setInProgress, setAutoPlayInProgress]);
    var isNextBetAllowed = useCallback(function () {
        if (playMode === PlayModeType.Auto) {
            if (autoPlayOptions.betCount <= 0 && autoPlayOptions.betCount !== -1) {
                return false;
            }
            var moneyDiff = (balance - initialAutoPlayBalance.current) / 10000;
            if ((autoPlayOptions.stopWin > 0 && moneyDiff >= autoPlayOptions.stopWin)
                || (autoPlayOptions.stopLoss > 0 && -moneyDiff >= autoPlayOptions.stopLoss)) {
                return false;
            }
        }
        var currentBalance = balance / 10000;
        var requiredBalance = betAmount;
        if (currentBalance < requiredBalance) {
            return false;
        }
        return true;
    }, [
        autoPlayInProgress,
        autoPlayOptions,
        balance,
        betAmount,
        initialAutoPlayBalance,
        playMode,
    ]);
    var makeNextBet = useCallback(function () {
        setAutoPlayNextBet(false);
        // Early return if bet not allowed or auto play stopped
        if (!isNextBetAllowed() || (playMode === PlayModeType.Auto && stopAutoPlay.current)) {
            finalizeAutoBet();
            return;
        }
        // Handle auto play mode
        if (playMode === PlayModeType.Auto) {
            // Decrement bet count if not unlimited (-1)
            setAutoPlayOptions(function (prevOptions) { return (__assign(__assign({}, prevOptions), { betCount: prevOptions.betCount !== -1 ? prevOptions.betCount - 1 : -1 })); });
            setBetAmount(function (prev) {
                // Calculate new bet amount based on previous result
                var betDeltaWinAction = autoPlayOptions.betDeltaWinAction, betDeltaLossAction = autoPlayOptions.betDeltaLossAction, betDeltaWin = autoPlayOptions.betDeltaWin, betDeltaLoss = autoPlayOptions.betDeltaLoss;
                // Determine if previous bet was a win
                var hasPreviousBet = prevAutoPlayWinnings !== null;
                var isWin = hasPreviousBet && prevAutoPlayWinnings > betAmount;
                var newBetAmount = initialAutoPlayBetAmount.current;
                if (hasPreviousBet) {
                    if (isWin && betDeltaWinAction === BetIncreaseSelect.IncreaseBy) {
                        newBetAmount = parseFloat(getFloat(prev + (prev / 100) * betDeltaWin));
                    }
                    else if (!isWin && betDeltaLossAction === BetIncreaseSelect.IncreaseBy) {
                        newBetAmount = parseFloat(getFloat(prev + (prev / 100) * betDeltaLoss));
                    }
                }
                return newBetAmount;
            });
        }
        // Start the game
        setInProgress(true);
        if (playMode === PlayModeType.Auto) {
            setAutoPlayInProgress(true);
            setAutoPlayStartGame(true);
        }
    }, [
        setInProgress,
        playMode,
        betAmount,
        autoPlayOptions,
        isNextBetAllowed,
        setAutoPlayStartGame,
        finalizeAutoBet,
    ]);
    var handleBetButton = function () {
        if (autoPlayInProgress || inProgress) {
            finalizeAutoBet();
            return;
        }
        if (playMode === PlayModeType.Auto) {
            stopAutoPlay.current = false;
            initialAutoPlayBalance.current = balance;
            initialAutoPlayBetCount.current = autoPlayOptions.betCount;
            initialAutoPlayBetAmount.current = betAmount;
        }
        makeNextBet();
    };
    useEffect(function () {
        setBalance(initialBalance);
    }, [initialBalance]);
    useEffect(function () {
        setGameResults(initialGameResults);
    }, [initialGameResults]);
    useEffect(function () {
        if (autoPlayNextBet) {
            makeNextBet();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        autoPlayNextBet,
        // makeNextBet,
    ]);
    /**
     * Add a tooltip to the bet button
     * @param tooltip - The tooltip to add
     * @param priority - The priority of the tooltip (0 is highest priority)
     */
    var addBetButtonTooltip = useCallback(function (tooltip, priority) {
        if (priority === void 0) { priority = 0; }
        setBetButtonTooltip(function (prev) {
            // Convert existing tooltips to objects with reversed indices
            var tooltipObjects = prev.map(function (t, i) { return ({
                tooltip: t,
                index: prev.length - 1 - i
            }); });
            // Remove existing tooltip if present
            var filteredTooltips = tooltipObjects.filter(function (t) { return t.tooltip !== tooltip; });
            // Add new tooltip with specified priority
            var newTooltips = __spreadArray(__spreadArray([], filteredTooltips, true), [{ tooltip: tooltip, index: priority }], false);
            // Sort by index and map back to string array
            return newTooltips
                .sort(function (a, b) { return b.index - a.index; })
                .map(function (t) { return t.tooltip; });
        });
    }, [setBetButtonTooltip]);
    var removeBetButtonTooltip = useCallback(function (tooltip) {
        if (tooltip === void 0) { tooltip = undefined; }
        setBetButtonTooltip(function (prev) { return tooltip ? prev.filter(function (t) { return t !== tooltip; }) : []; });
    }, [setBetButtonTooltip]);
    var getBetButtonTooltip = useCallback(function () {
        return betButtonTooltip.length > 0 ? betButtonTooltip[betButtonTooltip.length - 1] : null;
    }, [betButtonTooltip]);
    useEffect(function () {
        var NOT_ENOUGH_BALANCE_TOOLTIP = "Not enough balance";
        if (balance / 10000 < betAmount) {
            addBetButtonTooltip(NOT_ENOUGH_BALANCE_TOOLTIP, 0);
        }
        else {
            removeBetButtonTooltip(NOT_ENOUGH_BALANCE_TOOLTIP);
        }
    }, [betAmount, balance]);
    return (_jsx(GameContext.Provider, { value: {
            constants: constants,
            boardSize: boardSize,
            setBoardSize: setBoardSize,
            balance: balance,
            setBalance: setBalance,
            betAmount: betAmount,
            setBetAmount: setBetAmount,
            playMode: playMode,
            setPlayMode: setPlayMode,
            prediction: prediction,
            setPrediction: setPrediction,
            payout: payout,
            winChance: winChance,
            inProgress: inProgress,
            setInProgress: setInProgress,
            gameResults: gameResults,
            rotateGameResults: rotateGameResults,
            minBet: minBet,
            maxBet: maxBet,
            autoPlayOptions: autoPlayOptions,
            setAutoPlayOptions: setAutoPlayOptions,
            autoPlayInProgress: autoPlayInProgress,
            updatePayout: updatePayout,
            updateWinChance: updateWinChance,
            payoutInput: payoutInput,
            setPayoutInput: setPayoutInput,
            winChanceInput: winChanceInput,
            setWinChanceInput: setWinChanceInput,
            randomSelect: randomSelect,
            setRandomSelect: setRandomSelect,
            cashout: cashout,
            setCashout: setCashout,
            cashoutMultiplier: cashoutMultiplier,
            setCashoutMultiplier: setCashoutMultiplier,
            setPrevAutoPlayWinnings: setPrevAutoPlayWinnings,
            setAutoPlayNextBet: setAutoPlayNextBet,
            autoPlayStartGame: autoPlayStartGame,
            setAutoPlayStartGame: setAutoPlayStartGame,
            handleBetButton: handleBetButton,
            stopAutoPlay: stopAutoPlay,
            initialAutoPlayBalance: initialAutoPlayBalance,
            controlPanelDisabled: controlPanelDisabled,
            setControlPanelDisabled: setControlPanelDisabled,
            betButtonShake: betButtonShake,
            setBetButtonShake: setBetButtonShake,
            addBetButtonTooltip: addBetButtonTooltip,
            removeBetButtonTooltip: removeBetButtonTooltip,
            getBetButtonTooltip: getBetButtonTooltip,
            clientSeed: clientSeed,
            setClientSeed: setClientSeed,
            customClientSeed: customClientSeed,
            setCustomClientSeed: setCustomClientSeed,
            generateClientSeed: generateClientSeed,
        }, children: children }));
};
//# sourceMappingURL=GameContext.js.map